/**
 * TypeScript types for recurring reminders
 * Matches the database schema from recurring_reminder_templates table
 */

export type RecurrencePattern = 'daily' | 'weekly' | 'monthly';
export type RecurrenceEndType = 'after_occurrences' | 'on_date' | 'never';

/**
 * Recurring reminder template from database
 */
export interface RecurringReminderTemplate {
  id: string;
  user_id: string;
  client_id: string;

  // Template content
  title: string;
  description: string | null;

  // Recurrence pattern
  recurrence_pattern: RecurrencePattern;
  recurrence_interval: number;
  weekdays: number[] | null; // ISO days: 1=Monday, 7=Sunday
  day_of_month: number | null; // 1-31

  // Timing
  start_date: string; // YYYY-MM-DD
  start_time: string; // HH:MM

  // End configuration
  end_type: RecurrenceEndType;
  end_after_occurrences: number | null;
  end_on_date: string | null; // YYYY-MM-DD

  // Instance tracking
  last_generated_date: string | null; // YYYY-MM-DD
  instances_generated: number;

  // Status
  is_active: boolean;
  paused_at: string | null; // ISO timestamp

  // Metadata
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  deleted_at: string | null; // ISO timestamp
}

/**
 * Recurring template with client information (for display)
 */
export interface RecurringTemplateWithClient extends RecurringReminderTemplate {
  client: {
    id: string;
    name: string;
    status: 'Lead' | 'Active' | 'Past';
    phone?: string;
  };
  next_occurrence?: string | null; // ISO timestamp of next pending instance
  next_occurrence_date?: string | null; // YYYY-MM-DD
}

/**
 * Helper type for displaying recurrence pattern in human-readable format
 */
export interface RecurrenceDescription {
  pattern: string; // e.g., "Every 2 weeks on Monday, Wednesday"
  nextOccurrences: Date[]; // Next N occurrences for preview
  endDescription: string; // e.g., "Ends after 10 occurrences" or "No end date"
}

/**
 * Weekday constants matching ISO standard (1=Mon, 7=Sun)
 */
export const WEEKDAYS = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

export const WEEKDAY_NAMES: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export const WEEKDAY_SHORT_NAMES: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

/**
 * Helper function to format recurrence pattern for display
 */
export function formatRecurrencePattern(template: RecurringReminderTemplate): string {
  const { recurrence_pattern, recurrence_interval, weekdays, day_of_month } = template;

  let pattern = '';

  if (recurrence_pattern === 'daily') {
    pattern = recurrence_interval === 1 ? 'Daily' : `Every ${recurrence_interval} days`;
  } else if (recurrence_pattern === 'weekly') {
    const dayNames = weekdays
      ?.map((day) => WEEKDAY_SHORT_NAMES[day])
      .sort((a, b) => {
        const aIndex = Object.values(WEEKDAY_SHORT_NAMES).indexOf(a);
        const bIndex = Object.values(WEEKDAY_SHORT_NAMES).indexOf(b);
        return aIndex - bIndex;
      })
      .join(', ');

    if (recurrence_interval === 1) {
      pattern = `Weekly on ${dayNames}`;
    } else {
      pattern = `Every ${recurrence_interval} weeks on ${dayNames}`;
    }
  } else if (recurrence_pattern === 'monthly') {
    const ordinal = getOrdinal(day_of_month!);
    pattern =
      recurrence_interval === 1
        ? `Monthly on the ${ordinal}`
        : `Every ${recurrence_interval} months on the ${ordinal}`;
  }

  return pattern;
}

/**
 * Helper function to format end condition for display
 */
export function formatEndCondition(template: RecurringReminderTemplate): string {
  const { end_type, end_after_occurrences, end_on_date } = template;

  if (end_type === 'never') {
    return 'No end date';
  } else if (end_type === 'after_occurrences') {
    return `Ends after ${end_after_occurrences} occurrence${end_after_occurrences === 1 ? '' : 's'}`;
  } else if (end_type === 'on_date') {
    return `Ends on ${formatDate(end_on_date!)}`;
  }

  return '';
}

/**
 * Helper to get ordinal suffix (1st, 2nd, 3rd, etc.)
 */
function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Helper to format date (YYYY-MM-DD to readable format)
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00'); // Prevent timezone shifts
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate next N occurrences for preview
 * Returns up to N future dates based on the recurrence pattern
 */
export function calculateNextOccurrences(
  template: RecurringReminderTemplate,
  count: number = 5
): Date[] {
  const occurrences: Date[] = [];
  const { recurrence_pattern, recurrence_interval, weekdays, day_of_month, start_date, end_type, end_after_occurrences, end_on_date } = template;

  let currentDate = new Date(start_date + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Start from today if start_date is in the past
  if (currentDate < today) {
    currentDate = new Date(today);
  }

  let generated = 0;
  const maxIterations = count * 100; // Safety cap
  let iterations = 0;

  while (generated < count && iterations < maxIterations) {
    iterations++;

    // Check end conditions
    if (end_type === 'on_date' && end_on_date) {
      const endDate = new Date(end_on_date + 'T00:00:00');
      if (currentDate > endDate) break;
    }

    if (end_type === 'after_occurrences' && end_after_occurrences && generated >= end_after_occurrences) {
      break;
    }

    // Pattern-specific logic
    if (recurrence_pattern === 'daily') {
      occurrences.push(new Date(currentDate));
      generated++;
      currentDate.setDate(currentDate.getDate() + recurrence_interval);
    } else if (recurrence_pattern === 'weekly') {
      const currentWeekday = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Convert to ISO

      if (weekdays?.includes(currentWeekday)) {
        occurrences.push(new Date(currentDate));
        generated++;
      }

      currentDate.setDate(currentDate.getDate() + 1);

      // Jump weeks if interval > 1
      if (currentWeekday === 7 && recurrence_interval > 1) {
        currentDate.setDate(currentDate.getDate() + (recurrence_interval - 1) * 7);
      }
    } else if (recurrence_pattern === 'monthly') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const safeDay = Math.min(day_of_month!, daysInMonth);

      const occurrenceDate = new Date(year, month, safeDay);

      if (occurrenceDate >= today) {
        occurrences.push(occurrenceDate);
        generated++;
      }

      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + recurrence_interval);
      currentDate.setDate(1); // Reset to first of month
    }
  }

  return occurrences;
}
